import React, { useMemo } from 'react';
import type { ScreenQuestion } from '../../../features/flow/types';
import type { SessionScreen } from '../../../features/flow/session';
import { cleanParagraph, cleanQuestionText, evaluateShowIf } from '../../../features/flow/utils';

function toDisplay(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' && val.trim() === '') return '';
  return String(val);
}

function sectionKind(headerText: string | undefined): 'tax' | 'deductions' | 'deadline' | 'other' {
  const t = String(headerText ?? '').toLowerCase();
  if (t.includes('tax calculation')) return 'tax';
  if (t.includes('deductions applied')) return 'deductions';
  if (t.includes('filing') || t.includes('payment')) return 'deadline';
  return 'other';
}

export function ReviewTaxSummaryScreenBody({
  sessionScreen,
  questions,
  answers,
  answersForShowIf,
}: {
  sessionScreen: SessionScreen;
  questions: ScreenQuestion[];
  answers: Record<string, unknown>;
  answersForShowIf: Record<string, any>;
}) {
  const paragraphs = useMemo(() => (sessionScreen.paragraphs ?? []).map((p) => String(p)), [sessionScreen.paragraphs]);

  const deductionsHint = useMemo(() => {
    const hintLine = paragraphs.find((p) => /clearly list deductions/i.test(p));
    return hintLine ? cleanParagraph(hintLine) : '';
  }, [paragraphs]);

  const { taxLines, deductionBullets, deadlineBullets } = useMemo(() => {
    const taxLines: Array<{ label: string; value: string }> = [];
    const deductionBullets: string[] = [];
    const deadlineBullets: string[] = [];

    let currentHeader: string | undefined;
    for (const q of questions) {
      if (q.field_type === 'tax_summary_section') {
        currentHeader = q.question_text;
        continue;
      }

      // Respect show_if so we don't show irrelevant lines.
      if (q.show_if && !evaluateShowIf(q.show_if, answersForShowIf)) continue;
      const kind = sectionKind(currentHeader);

      const label = cleanQuestionText(q.question_text ?? '', '');
      const currentValue = toDisplay(q.current_value ?? answers[q.id]);

      if (kind === 'tax') {
        if (q.field_type === 'tax_calculated' || q.field_type === 'tax_total' || q.field_type === 'review_display') {
          taxLines.push({ label, value: currentValue || '—' });
        }
        continue;
      }

      if (kind === 'deductions') {
        if (q.field_type === 'tax_breakdown') {
          const v = currentValue;
          deductionBullets.push(`${label}: ${v || '—'}`);
        }
        continue;
      }

      if (kind === 'deadline') {
        if (q.field_type === 'review_display') {
          deadlineBullets.push(`${label}: ${currentValue || '—'}`);
        }
      }
    }

    return { taxLines, deductionBullets, deadlineBullets };
  }, [answers, answersForShowIf, questions]);

  return (
    <div className="w-full flex flex-col items-start gap-6">
      <div className="w-full max-w-[520px] flex flex-col gap-4">
        <div className="text-sm font-semibold text-[#131315]">Tax Calculation:</div>
        <div className="flex flex-col gap-2">
          {taxLines.map((l) => (
            <div key={l.label} className="text-sm text-[#131315]">
              {l.label}: {l.value || '—'}
            </div>
          ))}
        </div>
      </div>

      <div className="w-full max-w-[520px] flex flex-col gap-3">
        <div className="text-sm font-semibold text-[#131315]">Deductions Applied:</div>
        {deductionsHint ? <div className="text-sm text-[#131315]">{deductionsHint}</div> : null}
        {deductionBullets.length > 0 ? (
          <ul className="list-disc list-inside text-sm text-[#131315]">
            {deductionBullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="w-full max-w-[520px] flex flex-col gap-3">
        <div className="text-sm font-semibold text-[#131315]">Filing &amp; Payment Deadline:</div>
        {deadlineBullets.length > 0 ? (
          <ul className="list-disc list-inside text-sm text-[#131315]">
            {deadlineBullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
